import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Wrench,
    Calendar,
    DollarSign,
    Box,
    FileText,
    Image as ImageIcon,
    CheckCircle2,
    User,
    Edit,
    Trash2,
    AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { RepairRecord } from '../../types/asset';
import { toast } from 'sonner';
import { toSydneyDate } from '../../utils/helpers';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { cn } from '../../components/ui/utils';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

const RepairDetails: React.FC = () => {
    const { id, repairId } = useParams<{ id: string, repairId: string }>();
    const navigate = useNavigate();
    const [repair, setRepair] = useState<RepairRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [approving, setApproving] = useState(false);
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { currentUser } = useAuth();
    const ability = useAbility(AbilityContext);

    const fetchRepairDetails = async () => {
        try {
            setLoading(true);
            const res = await api.get<RepairRecord>(`/api/assets/${id}/repair/${repairId}`);
            setRepair(res);
        } catch (error) {
            console.error('Error fetching repair details:', error);
            toast.error('Failed to load repair details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id && repairId) {
            fetchRepairDetails();
        }
    }, [id, repairId]);

    const handleApprove = async () => {
        try {
            setApproving(true);
            await api.patch(`/api/assets/${id}/repair/${repairId}/approve`, {});
            toast.success('Repair approved successfully');
            fetchRepairDetails();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to approve repair');
        } finally {
            setApproving(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await api.delete(`/api/assets/${id}/repair/${repairId}`);
            toast.success('Repair record deleted successfully');
            navigate(`/assets/${id}`);
        } catch (err: any) {
            console.error('Error deleting repair:', err);
            toast.error(err.response?.data?.message || 'Failed to delete repair');
            setDeleting(false);
            setShowDeleteAlert(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!repair) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">Repair record not found</h2>
                <Button onClick={() => navigate(`/assets/${id}`)} className="mt-4">Back to Asset</Button>
            </div>
        );
    }

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 bg-white"
                    onClick={() => navigate(`/assets/${id}`)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        Repair Details
                    </h1>
                    <p className="text-gray-500 text-sm font-medium mt-0.5">
                        Comprehensive view of the repair record #{(repair.id as any).toString().padStart(4, '0')}
                    </p>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                    {repair.status === 'Pending Approval' && ability.can(ACTIONS.APPROVE, RESOURCES.ASSET_REPAIR) && (
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl px-6 h-10 shadow-sm"
                            onClick={handleApprove}
                            disabled={approving}
                        >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {approving ? 'Approving...' : 'Approve Repair'}
                        </Button>
                    )}
                    {(repair.status === 'Pending Approval' 
                        ? ability.can(ACTIONS.UPDATE_PENDING_REPAIR, RESOURCES.ASSET_REPAIR) 
                        : ability.can(ACTIONS.UPDATE_APPROVED_REPAIR, RESOURCES.ASSET_REPAIR)) && (
                        <Button
                            variant="outline"
                            className="bg-white border-gray-200 text-gray-700 font-bold rounded-xl px-4 h-10 shadow-sm hover:bg-gray-50"
                            onClick={() => navigate(`/assets/${id}/repair/${repairId}/edit`)}
                        >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                        </Button>
                    )}
                    {(repair.status === 'Pending Approval' 
                        ? ability.can(ACTIONS.DELETE_PENDING_REPAIR, RESOURCES.ASSET_REPAIR) 
                        : ability.can(ACTIONS.DELETE_APPROVED_REPAIR, RESOURCES.ASSET_REPAIR)) && (
                        <Button
                            variant="outline"
                            className="bg-white border-gray-200 text-red-600 font-bold rounded-xl px-4 h-10 shadow-sm hover:bg-red-50 hover:border-red-100"
                            onClick={() => setShowDeleteAlert(true)}
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                        </Button>
                    )}
                    {repair.status === 'Pending Approval' && repair.approvedBy?.id && currentUser?.id && String(repair.approvedBy.id) === String(currentUser.id) && ability.can(ACTIONS.APPROVE, RESOURCES.ASSET_REPAIR) && (
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl px-6 h-10 shadow-sm"
                            onClick={handleApprove}
                            disabled={approving}
                        >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            {approving ? 'Approving...' : 'Approve'}
                        </Button>
                    )}
                </div>
            </div>

            <div className="space-y-6">
                {/* 1. Global Repair Information */}
                <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Wrench size={24} className="text-blue-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Repair Summary</h2>
                                <p className="text-gray-500 text-xs font-medium">Core details of the service</p>
                            </div>
                        </div>
                        <Badge className={cn("px-2 py-0.5 rounded-lg border-0 text-[10px] font-bold uppercase tracking-widest",
                            repair.status === 'Completed' ? "bg-green-100 text-green-700" :
                                repair.status === 'Pending Approval' ? "bg-amber-100 text-amber-700" :
                                    "bg-blue-100 text-blue-700"
                        )}>
                            {repair.status}
                        </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Date</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <Calendar size={14} className="text-blue-500" />
                                {toSydneyDate(repair.repairDate)}
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Cost</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <DollarSign size={14} className="text-blue-500" />
                                {Number(repair.cost || 0).toLocaleString()}
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Vendor</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <Box size={16} className="text-orange-500" />
                                {repair.vendor || 'N/A'}
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Initiated By</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <User size={16} className="text-blue-500" />
                                {repair.createdBy?.name || 'Unknown User'}
                            </div>
                        </div>
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Approver</span>
                            <div className="flex items-center gap-2 font-bold text-gray-900">
                                <User size={16} className="text-indigo-500" />
                                {repair.approvedBy?.name || 'Pending Approval'}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                        {repair.partsRepaired && (
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Parts Repaired</span>
                                <p className="text-sm font-medium text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    {repair.partsRepaired}
                                </p>
                            </div>
                        )}
                        {repair.description && (
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Description</span>
                                <p className="text-sm font-medium text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    {repair.description}
                                </p>
                            </div>
                        )}
                    </div>
                </section>

                {/* 2. Repair Attachments / Images */}
                {(repair as any).attachments && (repair as any).attachments.length > 0 && (
                    <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                        <div className="flex items-center gap-3">
                            <ImageIcon size={24} className="text-indigo-500" />
                            <div>
                                <h2 className="font-bold text-gray-900 text-lg">Images & Documents</h2>
                                <p className="text-gray-500 text-xs font-medium">Visual proof of repair</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-indigo-50/30 p-6 rounded-2xl border border-indigo-50/50">
                            {(repair as any).attachments.map((att: any, idx: number) => {
                                const isImage = att.mimetype.startsWith('image/');
                                return (
                                    <div
                                        key={idx}
                                        className={cn("rounded-2xl overflow-hidden border border-gray-200 group relative", isImage ? "aspect-square cursor-pointer" : "p-4 bg-white flex flex-col items-center justify-center")}
                                        onClick={() => {
                                            if (isImage) {
                                                setSelectedImage(att.url);
                                                setIsImageModalOpen(true);
                                            } else {
                                                window.open(att.url, '_blank');
                                            }
                                        }}
                                    >
                                        {isImage ? (
                                            <>
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10 flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 bg-white/90 rounded-full p-2 transform scale-75 group-hover:scale-100 transition-all duration-300">
                                                        <ImageIcon size={20} className="text-gray-700" />
                                                    </div>
                                                </div>
                                                <img src={att.url} alt={att.filename} className="w-full h-full object-cover" />
                                            </>
                                        ) : (
                                            <>
                                                <FileText size={32} className="text-gray-400 mb-2" />
                                                <span className="text-xs font-medium text-gray-600 text-center truncate w-full px-2" title={att.filename}>{att.filename}</span>
                                            </>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </section>
                )}
            </div>

            {/* Image Modal */}
            <Dialog open={isImageModalOpen} onOpenChange={setIsImageModalOpen}>
                <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden bg-transparent border-none shadow-none flex items-center justify-center">
                    <img
                        src={selectedImage}
                        alt="Repair Attachment Full View"
                        className="w-full h-auto max-h-[90vh] object-contain rounded-2xl ring-1 ring-white/10 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />
                </DialogContent>
            </Dialog>

            <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
                <AlertDialogContent className="rounded-[32px] border-gray-100 p-8">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <AlertTriangle className="text-red-500" />
                            Delete Repair Record
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-sm font-medium text-gray-500 mt-2">
                            Are you sure you want to delete this repair record? This action cannot be undone and will be permanently removed from the asset history.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-8 flex gap-3">
                        <AlertDialogCancel className="flex-1 rounded-2xl border-gray-100 bg-gray-50 text-gray-900 font-bold h-12 hover:bg-gray-100">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className="flex-1 rounded-2xl bg-red-600 text-white font-bold h-12 hover:bg-red-700 shadow-lg shadow-red-200"
                        >
                            {deleting ? 'Deleting...' : 'Confirm Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default RepairDetails;
